import * as members from 'privates/privateMembers';
import {onCustomValidation, getValidity, isValid, getValidationMessage, showValidityIndication, hideValidityIndication} from 'privates/validationsService/validationsService';

/**
 * @mixin ValidatableMixin
 * @summary Provides functionality for elements that can be validated.
 *
 * Certain input elements contain properties that are used in basic form
 * validation, such as [`required`]($w.RequiredMixin.html#required) and
 * [`maxlength`]($w.TextInputMixin.html#maxlength). More complex validation
 * logic is achieved using the properties and functions below.
 *
 * Basic validation of elements against the constraints set in the Editor or
 * programmatically is always peformed, regardless of any custom validation.
 *
 * However, sometimes more complex validation is needed, including validations
 * that depend on more than one element. This is typically achieved by adding
 * custom validation logic in an event handler that you set using the input
 * element's [`onCustomValidation()`](#onCustomValidation) function. Within that
 * handler, you call the `reject()` function to indicate that the element is
 * invalid. The element's validity is checked when the value of the element
 * changes either by user interaction or programmatically.
 *
 * Note that validations other than **required**, including custom validations,
 * are not run on input elements when they don't have a value.
 *
 * @memberof $w
 */
class ValidatableMixin {

    /**
     * @member valid
     * @label property
     * @syntax get valid(): boolean
     * @summary Indicates if an input element's value is valid.
     * @description
     *  The `valid` property indicates if an element's value satisfies
     *  all conditions to pass a validation check. This includes basic validity
     *  conditions, such as whether the element has a value if it is [required]($w.RequiredMixin.html#required),
     *  and those specified in its [`onCustomValidation()`](#onCustomValidation)
     *  event handler, if you defined one.
     *
     *  Note that validations other than **required**, including custom validations,
     *  are not run on input elements when they don't have a value.
     * @snippet [ValidatableMixin-valid.es6=Get whether the element is valid]
     * @type {external:Boolean}
     * @see [onCustomValidation( )](#onCustomValidation)
     * @memberof $w.ValidatableMixin
     * @readonly
     */
    get valid() {
        const compId = members._compId.get(this);

        return isValid(compId);
    }

    /**
     * @member validationMessage
     * @label property
     * @syntax
     * get validationMessage(): string
     * @summary Gets a message indicating why the element is invalid, or an
     *  empty string if the message is valid.
     * @description
     *  Set the value of the `validationMessage` property using the
     *  `reject()` function of the [`onCustomValidation()`](#onCustomValidation)
     *  event handler.
     * @snippet [ValidatableMixin-validationMessage.es6=Get the validation message]
     * @type {external:String}
     * @see [validity](#validity)
     * @memberof $w.ValidatableMixin
     * @readonly
     */
    get validationMessage() {
        const compId = members._compId.get(this);

        return getValidationMessage(compId);
    }

    /**
     * @typedef {external:Object} $w.ValidatableMixin~ValidityState
     * @summary Contains detailed information about the validity states of an element.
     * @description This object is returned by an element's
     *  [`validity`](#validity) property.
     * @property {external:Boolean} customError - `true` if the element's custom
     *  validity message has been set to a non-empty string.
     * @property {external:Boolean} valid - `true` if the element meets all
     *  constraint validations.
     * @property {external:Boolean} valueMissing - `true` if the element's
     *  [`required`]($w.RequiredElement.html#required) property is `true`, but
     *  it does not contain a value.
     * @property {external:Boolean} typeMismatch - `true` if the element's value
     *  does not match its type when the type is email or url.
     * @property {external:Boolean} patternMismatch - `true` if the element's
     *  value does not match its pattern validation.
     * @property {external:Boolean} tooLong - `true` if the length of the
     *  element's value exceeds its [`maxlength`]($w.TextInputElement.html#maxlength)
     *  property.
     * @property {external:Boolean} tooShort - `true` if the length of the
     *  element's value is smaller than its minlength.
     * @property {external:Boolean} rangeUnderflow - `true` if the element's
     *  value is less than the its [`min`]($w.TextInput.html#min) property.
     * @property {external:Boolean} rangeOverflow - `true` if the element's
     *  value is more than the its [`max`]($w.TextInput.html#max) property.
     * @property {external:Boolean} fileNotUploaded - `true` if the element is
     *  an upload button that is required and a file has been selected but not
     *  uploaded.
     * @property {external:Boolean} stepMismatch
     * @property {external:Boolean} badInput - `true` if the element's value
     *  cannot be converted to a value
     * @snippet [ValidityState.es6=Get ValidityState info]
     * @see [validity](#validity)
     */

    /**
     * @member validity
     * @label property
     * @syntax
     * get validity(): ValidityState
     * @summary Gets a ValidityState object that contains
     *  detailed information about the validity states of the element.
     * @snippet [ValidityState.es6=Log ValidityState info]
     * @type {$w.ValidatableMixin~ValidityState}
     * @see [validationMessage](#validationMessage)
     * @memberof $w.ValidatableMixin
     * @readonly
     */
    get validity() {
        const compId = members._compId.get(this);

        return getValidity(compId);
    }

    /**
     * @callback $w.Validator
     * @summary Function that runs when custom validation is checked.
     * @param {external:String|$w.UploadButton~File[]|external:Boolean} value The value of the element being validated.
     * @param {external:Function} reject A function that invalidates the element with the specified message.
     */

    /**
     * @function onCustomValidation
     * @syntax
     * function onCustomValidation(validator: Validator): void
     * callback Validator(value: string | File[] | boolean, reject: Function): void
     * @summary Adds an event handler that runs when the element's validation is checked.
     * @description
     *  The `onCustomValidation()` function allows you perform custom validation
     *  in addition to any basic validation that was defined in the Editor.
     *
     *  To invalidate the element, call the `reject()` function that is passed
     *  into the `validator` callback function and pass it a validation message.
     *
     *  The element's validity is checked when the value of the element
     *  changes either by user interaction or programmatically.
     *
     *  Note that validations other than **required**, including custom validations,
     *  are not run on input elements when they don't have a value.
     * @snippet [ValidatableMixin-onCustomValidation.es6=Set an element to invalid if its value is "evil"]
     * @param {$w.Validator} validator The name of the function or
     *  the function expression to run when the element's custom validation is checked.
     * @memberof $w.ValidatableMixin
     */
    onCustomValidation(validator) { //eslint-disable-line jsdoc-events
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        onCustomValidation(RMI, compId, validator);
    }


    /**
     * @function updateValidityIndication
     * @syntax
     * function updateValidityIndication(): void
     * @summary Updates the element's visual validity indication based on its current validity state.
     * @description
     *  Many elements have a visual cue that indicates whether they are valid or
     *  not. For example, a text input that usually has a black outline might have
     *  a red outline when its value is not valid.
     * @snippet [ValidatableMixin-updateValidityIndication.es6=Update an element's validatity indicator]
     * @memberof $w.ValidatableMixin
     */
    updateValidityIndication() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        showValidityIndication(RMI, compId);
    }

    /**
     * @function resetValidityIndication
     * @syntax
     * function resetValidityIndication(): void
     * @summary Resets the element's visual validity indication.
     * @description
     *  Many elements have a visual cue that indicates whether they are valid or
     *  not. For example, a text input that usually has a black outline might have
     *  a red outline when its value is not valid.
     *
     *  The `resetValidityIndication()` function resets the validity indication
     *  of an element to show the element as valid. The actual validity state of
     *  the element is not affected. If the element was invalid, it remains
     *  invalid. The validity indication shows the element as valid until
     *  the element's validity is checked when the value of the element
     *  changes either by user interaction or programmatically. At that point,
     *  the element's validity indication shows the element as invalid if
     *  its value is not valid.
     * @memberof $w.ValidatableMixin
     */
    resetValidityIndication() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        hideValidityIndication(RMI, compId);
    }
}

export default ValidatableMixin;



// WEBPACK FOOTER //
// ./js/modules/ui/mixins/ValidatableMixin.es6